import { useState } from 'react'

import { type FillCheck, FillFieldKey } from '@/models/fillApplication'
import type { BusinessCardForm } from '../App/Customers/FillApplication/useBusinessCardForm'
import type { CoverageCardForm } from '../App/Customers/FillApplication/useCoverageCardForm'

interface FormPart {
  /** defaultValues are what was last saved or loaded: Save, Cancel and fresh server values all reset to them. */
  form: { formState: { defaultValues?: object } }
  changedFields: readonly string[]
}

function fieldsByKey(
  business: BusinessCardForm,
  coverage: CoverageCardForm
): [FillFieldKey, FormPart, string[]][] {
  return [
    [FillFieldKey.COMPANY_NAME, business.details, ['name']],
    [FillFieldKey.ENTITY_TYPE, business.details, ['entityType']],
    [FillFieldKey.ADDRESS, business.details, ['line1', 'line2', 'city', 'state', 'zip']],
    [FillFieldKey.CA_NUMBER, business.details, ['caNumber']],
    [FillFieldKey.CANCELLATION_REASON, business.details, ['cancellationReason']],
    [FillFieldKey.OWNER_NAME, business.owner, ['firstName', 'lastName']],
    [FillFieldKey.OWNER_EMAIL, business.owner, ['email']],
    [FillFieldKey.OWNER_DOB, business.owner, ['dob']],
    [FillFieldKey.OWNER_MARITAL_STATUS, business.owner, ['maritalStatus']],
    [FillFieldKey.TRAVEL_DISTANCE, business.distance, ['travelDistance']],
    [FillFieldKey.FREIGHT, business.freight, ['commodities']],
    [FillFieldKey.START_DATE, coverage.coverage, ['startDate']],
    [FillFieldKey.COVERAGES, coverage.coverage, ['coverages']],
  ]
}

interface FieldsState {
  /** The fields' saved values, comparable as strings. */
  saved: string
  /** One of them holds an unsaved edit. */
  edited: boolean
}

function fieldsStateByKey(
  business: BusinessCardForm,
  coverage: CoverageCardForm
): Record<string, FieldsState> {
  return Object.fromEntries(
    fieldsByKey(business, coverage).map(([key, part, fields]) => {
      const saved = (part.form.formState.defaultValues ?? {}) as Record<string, unknown>
      return [
        key,
        {
          saved: JSON.stringify(fields.map((field) => saved[field])),
          edited: fields.some((field) => part.changedFields.includes(field)),
        },
      ]
    })
  )
}

/**
 * The checks still standing. A check whose field holds an unsaved edit, or was saved since the server's
 * checks came, waits for the server's next checks, so a fixed field's error goes as it's fixed without
 * flashing back before the refetch; undoing the edit brings it back.
 */
export function useOpenChecks(
  checks: FillCheck[],
  business: BusinessCardForm,
  coverage: CoverageCardForm
): FillCheck[] {
  const fields = fieldsStateByKey(business, coverage)
  const saved = Object.fromEntries(Object.entries(fields).map(([key, state]) => [key, state.saved]))
  // What was saved when these checks came: a later save is one the server hasn't looked at yet.
  const [checked, setChecked] = useState({ checks, saved })
  if (checked.checks !== checks) setChecked({ checks, saved })
  const checkedSaved = checked.checks === checks ? checked.saved : saved

  return checks.filter((check) => {
    const state = fields[check.field] as FieldsState | undefined
    // A roster or a field this page doesn't edit stands as the server says.
    if (!state) return true
    return !state.edited && state.saved === checkedSaved[check.field]
  })
}
